import React, { useState, useEffect, useCallback } from 'react';
import {
  getGrupos, crearGrupo, actualizarGrupo, eliminarGrupo,
  addAdminToGrupo, removeAdminFromGrupo,
  addComunidadToGrupo, removeComunidadFromGrupo,
  getAdmins, getComunidades, crearUsuario, eliminarUsuario
} from '../services/api';
import { useToast } from '../components/Toast';
import './GestionAdmins.css';

export default function GestionAdmins({ user }) {
  const { showToast } = useToast();
  const [grupos, setGrupos] = useState([]);
  const [sinGrupo, setSinGrupo] = useState([]);
  const [todasComunidades, setTodasComunidades] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modales
  const [modalNuevoGrupo, setModalNuevoGrupo] = useState(false);
  const [nombreGrupo, setNombreGrupo] = useState('');
  const [modalRenombrar, setModalRenombrar] = useState(null);
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [modalNuevoAdmin, setModalNuevoAdmin] = useState(false);
  const [nuevoAdmin, setNuevoAdmin] = useState({ nombre: '', apellidos: '', email: '', password: '' });
  const [creandoAdmin, setCreandoAdmin] = useState(false);

  const cargarDatos = useCallback(async () => {
    try {
      const [gruposRes, comRes] = await Promise.all([
        getGrupos(),
        getComunidades(),
      ]);
      setGrupos(gruposRes.data.grupos || []);
      setSinGrupo(gruposRes.data.sin_grupo || []);
      setTodasComunidades(comRes.data || []);
    } catch (e) {
      showToast('Error al cargar datos', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  const handleCrearGrupo = async (e) => {
    e.preventDefault();
    if (!nombreGrupo.trim()) return;
    try {
      await crearGrupo({ nombre: nombreGrupo.trim() });
      showToast('Grupo creado', 'success');
      setModalNuevoGrupo(false);
      setNombreGrupo('');
      cargarDatos();
    } catch { showToast('Error al crear grupo', 'error'); }
  };

  const handleRenombrar = async (e) => {
    e.preventDefault();
    if (!nuevoNombre.trim()) return;
    try {
      await actualizarGrupo(modalRenombrar.id, { nombre: nuevoNombre.trim() });
      showToast('Grupo renombrado', 'success');
      setModalRenombrar(null);
      cargarDatos();
    } catch { showToast('Error al renombrar', 'error'); }
  };

  const handleEliminarGrupo = async (grupo) => {
    if (!window.confirm(`¿Eliminar el grupo "${grupo.nombre}"? Los admins pasarán a tener acceso total.`)) return;
    try {
      await eliminarGrupo(grupo.id);
      showToast('Grupo eliminado', 'success');
      cargarDatos();
    } catch { showToast('Error al eliminar grupo', 'error'); }
  };

  const handleToggleAsignacion = async (grupo) => {
    const nuevoValor = !grupo.asignacion_proveedores_activa;
    try {
      await actualizarGrupo(grupo.id, { asignacion_proveedores_activa: nuevoValor });
      showToast(nuevoValor ? 'Asignación de proveedores activada' : 'Asignación de proveedores desactivada', 'success');
      cargarDatos();
    } catch { showToast('Error al cambiar configuración', 'error'); }
  };

  const handleAddAdmin = async (grupoId, adminId) => {
    if (!adminId) return;
    try {
      await addAdminToGrupo(grupoId, Number(adminId));
      showToast('Admin añadido al grupo', 'success');
      cargarDatos();
    } catch (e) {
      showToast(e.response?.data?.error || 'Error al añadir admin', 'error');
    }
  };

  const handleRemoveAdmin = async (grupoId, adminId, nombre) => {
    if (!window.confirm(`¿Quitar a ${nombre} del grupo? Pasará a tener acceso total.`)) return;
    try {
      await removeAdminFromGrupo(grupoId, adminId);
      showToast('Admin eliminado del grupo', 'success');
      cargarDatos();
    } catch { showToast('Error al quitar admin', 'error'); }
  };

  const handleAddComunidad = async (grupoId, comunidadId) => {
    if (!comunidadId) return;
    try {
      await addComunidadToGrupo(grupoId, Number(comunidadId));
      showToast('Comunidad añadida al grupo', 'success');
      cargarDatos();
    } catch { showToast('Error al añadir comunidad', 'error'); }
  };

  const handleRemoveComunidad = async (grupoId, comId, nombre) => {
    try {
      await removeComunidadFromGrupo(grupoId, comId);
      showToast(`"${nombre}" eliminada del grupo`, 'success');
      cargarDatos();
    } catch { showToast('Error al quitar comunidad', 'error'); }
  };

  const handleCrearAdmin = async (e) => {
    e.preventDefault();
    setCreandoAdmin(true);
    try {
      await crearUsuario({ ...nuevoAdmin, rol: 'administrador' });
      showToast('Administrador creado', 'success');
      setModalNuevoAdmin(false);
      setNuevoAdmin({ nombre: '', apellidos: '', email: '', password: '' });
      cargarDatos();
    } catch (e) {
      showToast(e.response?.data?.error || 'Error al crear administrador', 'error');
    } finally { setCreandoAdmin(false); }
  };

  const handleEliminarSuperadmin = async (admin) => {
    const nombre = `${admin.nombre} ${admin.apellidos || ''}`.trim();
    if (!window.confirm(`¿Eliminar la cuenta de ${nombre}? Esta acción no se puede deshacer.`)) return;
    try {
      await eliminarUsuario(admin.id);
      showToast(`Cuenta de ${nombre} eliminada`, 'success');
      cargarDatos();
    } catch (e) {
      showToast(e.response?.data?.error || 'Error al eliminar la cuenta', 'error');
    }
  };

  if (loading) return <div className="loading-container"><div className="spinner"></div></div>;

  return (
    <div className="ga-container">
      <div className="ga-header">
        <div>
          <h1>Gestión de Administradores</h1>
          <p>Organiza los administradores en grupos. Cada grupo accede solo a sus comunidades.</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={() => setModalNuevoAdmin(true)}>
            + Nuevo administrador
          </button>
          <button className="btn btn-primary" onClick={() => setModalNuevoGrupo(true)}>
            + Nuevo grupo
          </button>
        </div>
      </div>

      {grupos.length === 0 && (
        <div className="ga-empty">
          <p>No hay grupos creados todavía.</p>
          <p>Crea un grupo y añade administradores y comunidades.</p>
        </div>
      )}

      {grupos.map(grupo => (
        <div key={grupo.id} className="ga-grupo-card">
          <div className="ga-grupo-header">
            <h2 className="ga-grupo-nombre">{grupo.nombre}</h2>
            <div className="ga-grupo-actions">
              <button
                className="btn-icon"
                title="Renombrar"
                onClick={() => { setModalRenombrar(grupo); setNuevoNombre(grupo.nombre); }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
                Renombrar
              </button>
              <button
                className="btn-icon btn-icon-danger"
                title="Eliminar grupo"
                onClick={() => handleEliminarGrupo(grupo)}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                  <path d="M10 11v6"/><path d="M14 11v6"/>
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                </svg>
                Eliminar
              </button>
            </div>
          </div>

          <div className="ga-grupo-section">
            <span className="ga-section-label">Administradores</span>
            <div className="ga-chips-row">
              {(grupo.admins || []).map(admin => (
                <span key={admin.id} className="ga-chip">
                  {admin.nombre} {admin.apellidos}
                  <button
                    className="ga-chip-remove"
                    onClick={() => handleRemoveAdmin(grupo.id, admin.id, `${admin.nombre} ${admin.apellidos}`)}
                  >×</button>
                </span>
              ))}
              <AddDropdown
                placeholder="+ Añadir admin"
                options={sinGrupo}
                getLabel={a => `${a.nombre} ${a.apellidos}`}
                onSelect={id => handleAddAdmin(grupo.id, id)}
              />
            </div>
          </div>

          <div className="ga-grupo-section">
            <span className="ga-section-label">Comunidades</span>
            <div className="ga-chips-row">
              {(grupo.comunidades || []).map(com => (
                <span key={com.id} className="ga-chip ga-chip-com">
                  {com.nombre}
                  <button
                    className="ga-chip-remove"
                    onClick={() => handleRemoveComunidad(grupo.id, com.id, com.nombre)}
                  >×</button>
                </span>
              ))}
              <AddDropdown
                placeholder="+ Añadir comunidad"
                options={todasComunidades.filter(c => !(grupo.comunidades || []).some(gc => gc.id === c.id))}
                getLabel={c => c.nombre}
                onSelect={id => handleAddComunidad(grupo.id, id)}
              />
            </div>
          </div>

          <div className="ga-grupo-section" style={{ borderTop: '1px solid var(--border)', paddingTop: '12px', marginTop: '4px' }}>
            <span className="ga-section-label">Módulos</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'var(--bg-secondary)', borderRadius: '8px', padding: '8px 14px' }}>
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Asignación de proveedores</span>
                <button
                  type="button"
                  onClick={() => handleToggleAsignacion(grupo)}
                  style={{
                    padding: '4px 14px',
                    borderRadius: '6px',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '12px',
                    background: grupo.asignacion_proveedores_activa ? '#10b981' : '#ef4444',
                    color: 'white',
                    minWidth: '80px',
                  }}
                >
                  {grupo.asignacion_proveedores_activa ? 'Activado' : 'Desactivado'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}

      <div className="ga-sin-grupo">
        <div className="ga-grupo-header">
          <span className="ga-section-label" style={{ margin: 0 }}>Superadmins (sin grupo — acceso total):</span>
          <button className="btn btn-secondary" style={{ fontSize: '0.82rem', padding: '6px 14px' }} onClick={() => setModalNuevoAdmin(true)}>
            + Añadir superadmin
          </button>
        </div>
        <div className="ga-chips-row" style={{ marginTop: 8 }}>
          {sinGrupo.length === 0 && (
            <span style={{ fontSize: '0.88rem', color: 'var(--text-secondary)' }}>No hay superadmins. Crea uno con el botón de arriba.</span>
          )}
          {sinGrupo.map(a => (
            <span key={a.id} className="ga-chip ga-chip-super">
              {a.nombre} {a.apellidos}
              {a.id !== user?.id && (
                <button
                  className="ga-chip-remove"
                  title="Eliminar cuenta"
                  onClick={() => handleEliminarSuperadmin(a)}
                >×</button>
              )}
            </span>
          ))}
        </div>
      </div>

      {/* Modal nuevo grupo */}
      {modalNuevoGrupo && (
        <div className="ga-modal-overlay" onClick={() => setModalNuevoGrupo(false)}>
          <div className="ga-modal" onClick={e => e.stopPropagation()}>
            <h3>Nuevo grupo</h3>
            <form onSubmit={handleCrearGrupo}>
              <div className="form-group">
                <label>Nombre del grupo</label>
                <input
                  className="form-control"
                  value={nombreGrupo}
                  onChange={e => setNombreGrupo(e.target.value)}
                  placeholder="Ej: Zona Norte, Empresa García..."
                  autoFocus required
                />
              </div>
              <div className="ga-modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setModalNuevoGrupo(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Crear grupo</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal renombrar */}
      {modalRenombrar && (
        <div className="ga-modal-overlay" onClick={() => setModalRenombrar(null)}>
          <div className="ga-modal" onClick={e => e.stopPropagation()}>
            <h3>Renombrar grupo</h3>
            <form onSubmit={handleRenombrar}>
              <div className="form-group">
                <label>Nuevo nombre</label>
                <input
                  className="form-control"
                  value={nuevoNombre}
                  onChange={e => setNuevoNombre(e.target.value)}
                  autoFocus required
                />
              </div>
              <div className="ga-modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setModalRenombrar(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal nuevo admin */}
      {modalNuevoAdmin && (
        <div className="ga-modal-overlay" onClick={() => setModalNuevoAdmin(false)}>
          <div className="ga-modal" onClick={e => e.stopPropagation()}>
            <h3>Nuevo administrador</h3>
            <form onSubmit={handleCrearAdmin}>
              <div className="form-group">
                <label>Nombre</label>
                <input className="form-control" value={nuevoAdmin.nombre}
                  onChange={e => setNuevoAdmin({ ...nuevoAdmin, nombre: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Apellidos</label>
                <input className="form-control" value={nuevoAdmin.apellidos}
                  onChange={e => setNuevoAdmin({ ...nuevoAdmin, apellidos: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input className="form-control" type="email" value={nuevoAdmin.email}
                  onChange={e => setNuevoAdmin({ ...nuevoAdmin, email: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Contraseña</label>
                <input className="form-control" type="password" value={nuevoAdmin.password}
                  onChange={e => setNuevoAdmin({ ...nuevoAdmin, password: e.target.value })}
                  minLength={6} required />
              </div>
              <div className="ga-modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setModalNuevoAdmin(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={creandoAdmin}>
                  {creandoAdmin ? 'Creando...' : 'Crear administrador'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function AddDropdown({ placeholder, options, getLabel, onSelect }) {
  const [open, setOpen] = useState(false);
  if (options.length === 0) return null;
  return (
    <div style={{ position: 'relative' }}>
      <button className="ga-add-btn" type="button" onClick={() => setOpen(o => !o)}>
        {placeholder}
      </button>
      {open && (
        <div className="ga-dropdown">
          {options.map(opt => (
            <button key={opt.id} className="ga-dropdown-item" type="button"
              onClick={() => { onSelect(opt.id); setOpen(false); }}>
              {getLabel(opt)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
